import React from 'react';
import { apiFetch } from './apiClient';

// Two things the whole app needs to know right after sign-in: who the current user is (for their
// role -- which lives in our database, not the Entra token) and whether maintenance mode is on.
// Both are fetched once, in parallel, and shared -- otherwise the nav, the maintenance gate, and
// the admin page would each fetch them independently.
//
// This is a small React context, not a state-management library -- CLAUDE.md Section 6 rules out
// adding one of those retroactively without a decision doc. Same spirit as apiClient.js: one
// place for a cross-cutting concern, no broader framework.
const AppContext = React.createContext(null);

// Denies everything: the shape my-permissions returns, used as the value when that call fails so a
// failure hides manager-only nav rather than exposing it.
const NO_PERMISSIONS = { canEditForecasts: false, canEditRnd: false,isManagerOfAnyone: false, isProjectManagerOfAny: false };

export function useApp() {
  const ctx = React.useContext(AppContext);
  if (!ctx) throw new Error('useApp must be used inside <AppProvider>');
  return ctx;
}

export function AppProvider({ children }) {
  const [state, setState] = React.useState({
    loading: true,
    error: '',
    currentUser: null,
    maintenance: null,
    permissions: NO_PERMISSIONS,
  });

  const load = React.useCallback(async () => {
    try {
      // The first two endpoints are @AllowDuringMaintenance on the API, so this still succeeds for a
      // locked-out member -- which is exactly how they get the message to display. my-permissions
      // isn't, so it's caught separately: a member locked out by maintenance must still reach the
      // maintenance message rather than the context's error state.
      const [currentUser, maintenance, permissions] = await Promise.all([
        apiFetch('/auth/me'),
        apiFetch('/maintenance'),
        apiFetch('/resource-assignments/my-permissions').catch(() => NO_PERMISSIONS),
      ]);
      setState({ loading: false, error: '', currentUser, maintenance, permissions });
    } catch (err) {
      setState({
        loading: false, error: err.message, currentUser: null, maintenance: null,
        permissions: NO_PERMISSIONS,
      });
    }
  }, []);

  React.useEffect(() => {
    load();
  }, [load]);

  // `refresh` lets the admin page update shared state after toggling maintenance, so the banner
  // and gate react immediately instead of waiting for a page reload.
  const value = React.useMemo(() => ({ ...state, refresh: load }), [state, load]);

  return <AppContext.Provider value={value}>{children}</AppContext.Provider>;
}
